import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";

const TRADERS_LIST_URL = "/trade/traders-list/";
const ASSIGN_RA_URL = "/trade/assign-ra/";
const ADVISORS_LIST_URL = "/tnibroadmin/advisors-list";

const thSx = {
  background: "#04343a",
  color: "#fff",
  fontWeight: 700,
  fontSize: "12px",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.15)",
  "&:last-child": { borderRight: "none" },
  py: "8px",
  px: "10px",
};

const tdSx = {
  borderRight: "1px solid #e2e8f0",
  "&:last-child": { borderRight: "none" },
  py: "8px",
  px: "10px",
};

const useAdvisorsList = () =>
  useQuery({
    queryKey: ["tni-advisors-list"],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await axiosConfig({ method: "get", url: ADVISORS_LIST_URL });
      return res?.data?.response || [];
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Failed to fetch advisors list");
    },
  });

const TNITrading = () => {
  const { data: advisors = [] } = useAdvisorsList();

  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTrader, setAssignTrader] = useState(null);
  const [selectedAdvisors, setSelectedAdvisors] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const fetchTraders = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: TRADERS_LIST_URL });
      setTraders(res?.data?.data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to fetch traders",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTraders();
  }, []);

  const selectAssignTrader = (trader) => {
    setAssignTrader(trader || null);
    const preSelected = (trader?.ras || []).map((ra) => {
      const match = advisors.find((a) => a.id === ra.ra_id);
      return match || { id: ra.ra_id, username: ra.ra_username, email: ra.ra_email };
    });
    setSelectedAdvisors(preSelected);
  };

  const openAssign = (trader) => {
    setAssignDialogOpen(true);
    selectAssignTrader(trader);
  };

  const closeAssign = () => {
    if (!assigning) {
      setAssignDialogOpen(false);
      setAssignTrader(null);
      setSelectedAdvisors([]);
    }
  };

  const handleAssign = async () => {
    if (!assignTrader) return toast.error("Select a trader first");
    setAssigning(true);
    try {
      const res = await axiosConfig({
        method: "patch",
        url: ASSIGN_RA_URL,
        data: {
          trader_id: assignTrader.trader_id,
          user_ids: selectedAdvisors.map((a) => a.id),
        },
      });
      toast.success(res?.data?.message || "RA(s) assigned");
      closeAssign();
      fetchTraders();
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to assign RA",
      );
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
        <Box
          sx={{
            background: "#04343a",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
            p: "20px 28px",
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ShowChartIcon sx={{ color: "#fff", fontSize: 32 }} />
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
                TNI Trading
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
                Manage traders and their assigned RAs
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => openAssign(null)}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            Assign RA
          </Button>
        </Box>

        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 48 }}>SNo.</TableCell>
                  <TableCell sx={thSx}>Trader</TableCell>
                  <TableCell sx={thSx}>Assigned RA(s)</TableCell>
                  <TableCell sx={{ ...thSx, width: 90, textAlign: "center" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 8 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : traders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 8 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                        No traders found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  traders.map((trader, idx) => (
                    <TableRow
                      key={trader.trader_id}
                      sx={{
                        "&:hover": { background: "#f8fafc" },
                        transition: "background 0.15s",
                        borderBottom: "1px solid #e2e8f0",
                        verticalAlign: "middle",
                      }}
                    >
                      <TableCell
                        sx={{ ...tdSx, color: "#94a3b8", fontSize: "13px", fontWeight: 500 }}
                      >
                        {idx + 1}
                      </TableCell>

                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#04343a" }}>
                          {trader.username}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          {trader.email}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...tdSx, py: 1 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(trader.ras || []).length === 0 ? (
                            <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>
                              No RA assigned
                            </Typography>
                          ) : (
                            trader.ras.map((ra) => (
                              <Tooltip key={ra.alignment_id} title={ra.ra_email}>
                                <Chip
                                  size="small"
                                  label={ra.ra_username}
                                  sx={{
                                    background: "#f0faf7",
                                    color: "#016551",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                  }}
                                />
                              </Tooltip>
                            ))
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                        <Tooltip title="Assign RA">
                          <IconButton
                            size="small"
                            onClick={() => openAssign(trader)}
                            sx={{
                              color: "#016551",
                              background: "#f0faf7",
                              "&:hover": { background: "#016551", color: "#fff" },
                              transition: "all 0.2s",
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Dialog
        open={assignDialogOpen}
        onClose={closeAssign}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Assign RA{assignTrader ? ` — ${assignTrader.username}` : ""}
          </Typography>
          <IconButton onClick={closeAssign} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              Trader
            </Typography>
            <Autocomplete
              options={traders}
              value={assignTrader}
              onChange={(_, newValue) => selectAssignTrader(newValue)}
              getOptionLabel={(option) => option.username || `#${option.trader_id}`}
              isOptionEqualToValue={(option, value) => option.trader_id === value.trader_id}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select a trader" size="small" />
              )}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              Research Analyst(s)
            </Typography>
            <Autocomplete
              multiple
              options={advisors}
              value={selectedAdvisors}
              onChange={(_, newValue) => setSelectedAdvisors(newValue)}
              getOptionLabel={(option) => option.username || option.email || `#${option.id}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select RA(s)"
                  size="small"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={closeAssign} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={assigning || !assignTrader}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {assigning ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TNITrading;
